import {
    Box,
    Button,
    Chip,
    Grid,
    LinearProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from '@mui/material';
import React, { useMemo } from 'react';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { ReactComponent as CampaignIcon } from 'assets/images/icons/customer/chiến dịch.svg';
import { ReactComponent as SurveyServiceIcon } from 'assets/images/icons/customer/dịch vụ khảo sát.svg';
import { ReactComponent as DataCollected } from 'assets/images/icons/customer/dữ liệu thu thập.svg';
import { ReactComponent as ServicePackageIcon } from 'assets/images/icons/customer/gói dịch vụ.svg';
import { ReactComponent as AccountIcon } from 'assets/images/icons/customer/tài khoản.svg';
import { common } from '@mui/material/colors';
import LineChart from 'common/components/charts/line-chart';
import { createStyleSheet, handleError } from 'utils/helpers';
import BarChart from 'common/components/charts/bar-chart';
import PieDoughnutChart from 'common/components/charts/pie-doughnut-chart';
import CampaignStatus from '../campaign/components/campaign-status';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PaymentStatus from '../payment/components/payment-status';
import { savePDF } from '@progress/kendo-react-pdf';
import {
    overviewCardInfo,
    overViewChartCollectData,
    overViewChartCampaignCollectData,
    overviewChartDevice,
    overviewChartArea,
    overviewChartAccount,
} from 'services/overview.service';
import { toast } from 'react-toastify';
import numeral from 'plugins/numeral';
import { listCampaigns } from 'services/campaign.service';
import { useAppSelector } from 'common/hook';
import {
    selectDashboardCardColors,
    selectChartDataCollectionColors,
    selectChartCampaignColors,
    selectChartDeviceColors,
    selectChartRegionColors,
    selectChartAccountColors,
} from 'common/theme-selectors';
import { convertValuePaymentType } from '../payment/constants';
import { listPayments } from 'services/payment.service';

const Overview: React.FC = () => {
    const userInfo = useAppSelector((state) => state.user);
    const userRole = useMemo(() => {
        return userInfo?.attributes.role;
    }, [userInfo]);
    const cardColors = useAppSelector(selectDashboardCardColors);
    const chartDataCollectionColors = useAppSelector(
        selectChartDataCollectionColors
    );
    const chartCampaignColors = useAppSelector(selectChartCampaignColors);
    const chartDeviceColors = useAppSelector(selectChartDeviceColors);
    const chartRegionColors = useAppSelector(selectChartRegionColors);
    const chartAccountColors = useAppSelector(selectChartAccountColors);
    const container = React.useRef(null);
    const [cardInfo, setCardInfo] = React.useState<{
        answers: {
            total: number;
            free: number;
            paid: number;
        };
        campaign: {
            total: number;
            free: number;
            paid: number;
        };
        plan: {
            total: number;
            totalMoney: number;
        };
        campaignService: {
            total: number;
            totalMoney: number;
        };
        account: {
            total: number;
            system: number;
            client: number;
        };
    }>({
        answers: {
            total: 0,
            free: 0,
            paid: 0,
        },
        campaign: {
            total: 0,
            free: 0,
            paid: 0,
        },
        plan: {
            total: 0,
            totalMoney: 0,
        },
        campaignService: {
            total: 0,
            totalMoney: 0,
        },
        account: {
            total: 0,
            system: 0,
            client: 0,
        },
    });
    const [chartCollectData, setChartCollectData] = React.useState<{
        xAxis: string[];
        data: {
            free: number[];
            paid: number[];
        };
    }>({
        xAxis: [],
        data: {
            free: [],
            paid: [],
        },
    });
    const [chartCampaignCollectData, setChartCampaignCollectData] =
        React.useState<{
            xAxis: string[];
            data: {
                campaign: number[];
                collect: number[];
            };
        }>({
            xAxis: [],
            data: {
                collect: [],
                campaign: [],
            },
        });
    const [chartDeviceData, setChartDeviceData] = React.useState<{
        xAxis: string[];
        data: {
            value: number;
            itemStyle: {
                color: string;
            };
        }[];
    }>({
        xAxis: [],
        data: [],
    });
    const [chartAreaData, setChartAreaData] = React.useState<{
        data: {
            name: string;
            value: number;
        }[];
    }>({
        data: [],
    });
    const [chartAccountData, setChartAccountData] = React.useState<{
        xAxis: string[];
        data: {
            system: number[];
            client: number[];
        };
    }>({
        xAxis: [],
        data: {
            system: [],
            client: [],
        },
    });
    const [listCampaignData, setListCampaignData] = React.useState<
        {
            id: string;
            name: string;
            campaignTime: string;
            status: string;
            progress: number;
            totalData: string;
        }[]
    >([]);
    const [listCampaignPage, setListCampaignPage] = React.useState(1);
    const [listPaymentData, setListPaymentData] = React.useState<
        {
            id: string;
            paymentType: string;
            productName: string;
            productNote: string;
            status: string;
        }[]
    >([]);
    const [listPaymentPage, setListPaymentPage] = React.useState(1);
    const exportPDFWithMethod = () => {
        const element = container.current || document.body;
        savePDF(element, {
            paperSize: 'auto',
            margin: 40,
            fileName: `Tổng quan`,
        });
    };
    const theme = useTheme();
    React.useEffect(() => {
        const fetchOverviewCardInfo = async () => {
            try {
                const response = await overviewCardInfo({});
                if (response.success) {
                    setCardInfo(response.data);
                } else {
                    toast.error(response.message);
                }
            } catch (error) {
                handleError(error);
            }
        };
        const fetchChartCollectData = async () => {
            try {
                const response = await overViewChartCollectData({});
                if (response.success) {
                    setChartCollectData(response.data);
                } else {
                    toast.error(response.message);
                }
            } catch (error) {
                handleError(error);
            }
        };
        const fetchChartCampaignCollectData = async () => {
            try {
                const response = await overViewChartCampaignCollectData({});
                if (response.success) {
                    setChartCampaignCollectData(response.data);
                } else {
                    toast.error(response.message);
                }
            } catch (error) {
                handleError(error);
            }
        };
        const fetchChartDevice = async () => {
            try {
                const response = await overviewChartDevice({});
                if (response.success) {
                    setChartDeviceData(response.data);
                } else {
                    toast.error(response.message);
                }
            } catch (error) {
                handleError(error);
            }
        };
        const fetchChartArea = async () => {
            try {
                const response = await overviewChartArea({});
                if (response.success) {
                    setChartAreaData(response.data);
                } else {
                    toast.error(response.message);
                }
            } catch (error) {
                handleError(error);
            }
        };
        const fetchChartAccount = async () => {
            try {
                const response = await overviewChartAccount({});
                if (response.success) {
                    setChartAccountData(response.data);
                } else {
                    toast.error(response.message);
                }
            } catch (error) {
                handleError(error);
            }
        };

        fetchOverviewCardInfo();
        fetchChartCollectData();
        fetchChartCampaignCollectData();
        fetchChartDevice();
        fetchChartArea();
        fetchChartAccount();
    }, []);

    React.useEffect(() => {
        const fetchListCampaign = async () => {
            try {
                const response = await listCampaigns({
                    page: listCampaignPage,
                    per_page: 5,
                    order_client_status: 'true',
                    q: {},
                });
                if (response.success) {
                    // push to list campaign
                    setListCampaignData((prev) => [
                        ...prev,
                        ...response.data.surveys.map((item) => {
                            const {
                                campaign_time,
                                title,
                                status_client,
                                total_answered,
                                total_answer_limit,
                            } = item.attributes;
                            return {
                                id: item.id,
                                campaignTime: campaign_time || '',
                                name: title,
                                status: status_client,
                                progress:
                                    total_answer_limit && total_answer_limit > 0
                                        ? ((total_answered || 0) /
                                              total_answer_limit) *
                                          100
                                        : 0,
                                totalData: `${total_answered}/${total_answer_limit}`,
                            };
                        }),
                    ]);
                } else {
                    toast.error(response.message);
                }
            } catch (error) {
                handleError(error);
            }
        };
        fetchListCampaign();
    }, [listCampaignPage]);

    React.useEffect(() => {
        const fetchListPayment = async () => {
            try {
                const response = await listPayments({
                    page: listPaymentPage,
                    per_page: 5,
                    q: {},
                });
                if (response.success) {
                    setListPaymentData((prev) => [
                        ...prev,
                        ...response.data.payments.map((item) => {
                            const { product_type, product, status } =
                                item.attributes;
                            return {
                                id: item.id,
                                paymentType: product_type,
                                productName: product?.name,
                                productNote: product?.note,
                                status,
                            };
                        }),
                    ]);
                } else {
                    toast.error(response.message);
                }
            } catch (error) {
                handleError(error);
            }
        };
        fetchListPayment();
    }, [listPaymentPage]);

    return (
        <Box mt={5}>
            <Box mb={'40px'}>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="end"
                >
                    <Typography
                        variant="h3"
                        component="h3"
                        fontSize={28}
                        fontWeight={'bold'}
                        lineHeight={'34px'}
                        color={theme.palette.primary.main}
                    >
                        Tổng quan
                    </Typography>
                    <Stack direction="row" spacing={2}>
                        <Button
                            onClick={exportPDFWithMethod}
                            variant="contained"
                            size="large"
                            sx={{
                                textTransform: 'none',
                                width: '177px',
                                height: '60px',
                                borderRadius: '39px',
                                padding: '0px',
                            }}
                        >
                            <Box
                                component={'div'}
                                width={'100%'}
                                height={'100%'}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: '11px',
                                }}
                            >
                                <FileDownloadOutlinedIcon
                                    sx={{
                                        fontSize: '40px !important',
                                        color: 'inherit',
                                    }}
                                />
                                <Typography
                                    fontSize={20}
                                    component={'span'}
                                    fontWeight={'bold'}
                                    lineHeight={'24px'}
                                >
                                    Xuất
                                </Typography>
                            </Box>
                        </Button>
                    </Stack>
                </Box>
                <Box ref={container}>
                    <Box
                        mt={3}
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 3,
                            mb: 4,
                        }}
                    >
                        {[
                            {
                                label: 'Dữ liệu thu thập',
                                value: {
                                    total: numeral(
                                        cardInfo.answers.total
                                    ).format(),
                                    free: numeral(
                                        cardInfo.answers.free
                                    ).format(),
                                    service: numeral(
                                        cardInfo.answers.paid
                                    ).format(),
                                },
                                color: cardColors.dataCollection,
                                icon: <DataCollected />,
                            },
                            {
                                label: 'Chiến dịch',
                                value: {
                                    total: numeral(
                                        cardInfo.campaign.total
                                    ).format(),
                                    free: numeral(
                                        cardInfo.campaign.free
                                    ).format(),
                                    service: numeral(
                                        cardInfo.campaign.paid
                                    ).format(),
                                },
                                color: cardColors.campaign,
                                icon: <CampaignIcon />,
                            },
                            {
                                label: 'Gói dịch vụ',
                                value: {
                                    total: numeral(
                                        cardInfo.plan.total
                                    ).format(),

                                    totalAmount: `${numeral(
                                        cardInfo.plan.totalMoney
                                    ).format()} VND`,
                                },
                                color: cardColors.servicePackage,
                                icon: <ServicePackageIcon />,
                            },
                            {
                                label: 'Dịch vụ khảo sát',
                                value: {
                                    total: numeral(
                                        cardInfo.campaignService.total
                                    ).format(),
                                    totalAmount: `${numeral(
                                        cardInfo.campaignService.totalMoney
                                    ).format()} VND`,
                                },
                                color: cardColors.surveyService,
                                icon: <SurveyServiceIcon />,
                            },
                        ].map((item, index) => (
                            <Box
                                key={index}
                                sx={{
                                    flex: 1,
                                    backgroundColor: item.color.bg,
                                    color: item.color.text,
                                    borderRadius: '11.67px',
                                    minHeight: '196px',
                                    p: 3,
                                }}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    spacing={1}
                                    mb={2}
                                >
                                    {item.icon}
                                    <Typography
                                        sx={{
                                            fontSize: '16px',
                                            fontWeight: 700,
                                            lineHeight: '19.36px',
                                        }}
                                    >
                                        {item.label}
                                    </Typography>
                                </Stack>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    spacing={1}
                                    mb={2}
                                >
                                    <Typography>Tổng</Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '16px',
                                            fontWeight: 700,
                                            lineHeight: '19.36px',
                                        }}
                                    >
                                        {item.value.total}
                                    </Typography>
                                </Stack>
                                {item.value.free && (
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        spacing={1}
                                        mb={2}
                                    >
                                        Miễn phí
                                        <Typography
                                            sx={{
                                                fontSize: '16px',
                                                fontWeight: 700,
                                                lineHeight: '19.36px',
                                            }}
                                        >
                                            {item.value.free}
                                        </Typography>
                                    </Stack>
                                )}
                                {item.value.service && (
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        spacing={1}
                                        mb={2}
                                    >
                                        Dịch vụ
                                        <Typography
                                            sx={{
                                                fontSize: '16px',
                                                fontWeight: 700,
                                                lineHeight: '19.36px',
                                            }}
                                        >
                                            {item.value.service}
                                        </Typography>
                                    </Stack>
                                )}
                                {item.value.totalAmount && (
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        spacing={1}
                                    >
                                        Tổng tiền
                                        <Typography
                                            sx={{
                                                fontSize: '16px',
                                                fontWeight: 700,
                                                lineHeight: '19.36px',
                                            }}
                                        >
                                            {item.value.totalAmount}
                                        </Typography>
                                    </Stack>
                                )}
                            </Box>
                        ))}
                        {userRole !== 'client' &&
                            [
                                {
                                    label: 'Tài khoản',
                                    value: {
                                        total: numeral(
                                            cardInfo.account.total
                                        ).format(),
                                        systemAccount: numeral(
                                            cardInfo.account.system
                                        ).format(),
                                        customer: numeral(
                                            cardInfo.account.client
                                        ).format(),
                                    },
                                    color: cardColors.account,
                                    icon: <AccountIcon />,
                                },
                            ].map((item, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        flex: 1,
                                        backgroundColor: item.color.bg,
                                        color: item.color.text,
                                        borderRadius: '11.67px',
                                        minHeight: '196px',
                                        p: 3,
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        spacing={1}
                                        mb={2}
                                    >
                                        {item.icon}
                                        <Typography
                                            sx={{
                                                fontSize: '16px',
                                                fontWeight: 700,
                                                lineHeight: '19.36px',
                                            }}
                                        >
                                            {item.label}
                                        </Typography>
                                    </Stack>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        spacing={1}
                                        mb={2}
                                    >
                                        <Typography>Tổng</Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '16px',
                                                fontWeight: 700,
                                                lineHeight: '19.36px',
                                            }}
                                        >
                                            {item.value.total}
                                        </Typography>
                                    </Stack>
                                    {item.value.systemAccount && (
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            spacing={1}
                                            mb={2}
                                        >
                                            Tài khoản hệ thống
                                            <Typography
                                                sx={{
                                                    fontSize: '16px',
                                                    fontWeight: 700,
                                                    lineHeight: '19.36px',
                                                }}
                                            >
                                                {item.value.systemAccount}
                                            </Typography>
                                        </Stack>
                                    )}
                                    {item.value.customer && (
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            spacing={1}
                                        >
                                            Khách hàng
                                            <Typography
                                                sx={{
                                                    fontSize: '16px',
                                                    fontWeight: 700,
                                                    lineHeight: '19.36px',
                                                }}
                                            >
                                                {item.value.customer}
                                            </Typography>
                                        </Stack>
                                    )}
                                </Box>
                            ))}
                    </Box>
                    <Grid container spacing={4}>
                        <Grid item xl={6} md={6}>
                            <Box
                                sx={{
                                    backgroundColor: common.white,
                                    padding: 4,
                                    minHeight: '468px',
                                    width: '100%',
                                    borderRadius: '30px',
                                }}
                            >
                                <Typography variant="h6" sx={styles.boxTitle}>
                                    Dữ liệu thu thập
                                </Typography>

                                <LineChart
                                    style={{ height: '400px', width: '100%' }}
                                    option={{
                                        color: chartDataCollectionColors,
                                        title: {
                                            text: '',
                                        },
                                        tooltip: {
                                            trigger: 'axis',
                                            axisPointer: {
                                                type: 'cross',
                                            },
                                        },

                                        toolbox: {
                                            show: false,
                                        },
                                        grid: {
                                            left: '0%',
                                            top: '5%',
                                            right: '5%',
                                            bottom: '15%',
                                            containLabel: true,
                                        },
                                        legend: {
                                            itemGap: 150,
                                            icon: 'square',
                                            top: 'bottom',
                                            data: [
                                                'Dữ liệu miễn phí',
                                                'Dữ liệu dịch vụ',
                                            ],
                                            itemWidth: 22,
                                            itemHeight: 22,
                                        },
                                        xAxis: {
                                            type: 'category',
                                            boundaryGap: false,
                                            data: chartCollectData.xAxis,
                                        },
                                        yAxis: {
                                            type: 'value',
                                        },
                                        series: [
                                            {
                                                name: 'Dữ liệu miễn phí',
                                                type: 'line',
                                                stack: 'Total',
                                                smooth: true,
                                                data: chartCollectData.data
                                                    .free,
                                            },
                                            {
                                                name: 'Dữ liệu dịch vụ',
                                                type: 'line',
                                                stack: 'Total',
                                                smooth: true,
                                                data: chartCollectData.data
                                                    .paid,
                                            },
                                        ],
                                    }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xl={6} md={6}>
                            <Box
                                sx={{
                                    backgroundColor: common.white,
                                    padding: 4,
                                    minHeight: '468px',
                                    width: '100%',
                                    borderRadius: '30px',
                                }}
                            >
                                <Typography variant="h6" sx={styles.boxTitle}>
                                    Chiến dịch với dữ liệu thu thập
                                </Typography>

                                <BarChart
                                    style={{ height: '400px', width: '100%' }}
                                    option={{
                                        color: chartCampaignColors,
                                        title: {
                                            text: '',
                                        },
                                        tooltip: {
                                            trigger: 'axis',
                                            axisPointer: {
                                                type: 'cross',
                                            },
                                        },
                                        grid: {
                                            left: '0%',
                                            right: '5%',
                                            top: '10%',
                                            bottom: '15%',
                                            containLabel: true,
                                        },
                                        legend: {
                                            itemGap: 150,
                                            icon: 'square',
                                            top: 'bottom',
                                            data: [
                                                'Chiến dịch',
                                                'Dữ liệu thu thập',
                                            ],
                                            itemWidth: 22,
                                            itemHeight: 22,
                                        },
                                        xAxis: {
                                            type: 'category',
                                            boundaryGap: true,
                                            data: chartCampaignCollectData.xAxis,
                                        },
                                        yAxis: {
                                            type: 'value',
                                        },
                                        series: [
                                            {
                                                name: 'Chiến dịch',
                                                type: 'bar',
                                                barWidth: '15%',
                                                barGap: '20%',
                                                data: chartCampaignCollectData
                                                    .data.campaign,
                                                itemStyle: {
                                                    borderRadius: [
                                                        50, 50, 0, 0,
                                                    ],
                                                },
                                            },
                                            {
                                                name: 'Dữ liệu thu thập',
                                                type: 'bar',
                                                barWidth: '15%',
                                                barGap: '20%',
                                                data: chartCampaignCollectData
                                                    .data.collect,
                                                itemStyle: {
                                                    borderRadius: [
                                                        50, 50, 0, 0,
                                                    ],
                                                },
                                            },
                                        ],
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid container marginTop={1} spacing={4}>
                        <Grid item md={userRole !== 'client' ? 4 : 6}>
                            <Box
                                sx={{
                                    minHeight: '414px',
                                    backgroundColor: common.white,
                                    padding: 4,
                                    borderRadius: '30px',
                                }}
                            >
                                <Typography variant="h6" sx={styles.boxTitle}>
                                    Thiết bị
                                </Typography>
                                <BarChart
                                    option={{
                                        color: chartDeviceColors,
                                        xAxis: {
                                            type: 'category',
                                            data: chartDeviceData.xAxis,
                                        },
                                        grid: {
                                            left: '0%',
                                            top: '15%',
                                            right: '5%',
                                            bottom: '10%',
                                            containLabel: true,
                                        },

                                        yAxis: {
                                            type: 'value',
                                            axisLabel: {
                                                formatter: (value: number) =>
                                                    numeral(value).format(
                                                        '0.0a'
                                                    ),
                                            },
                                        },
                                        series: [
                                            {
                                                data: chartDeviceData.data.map(
                                                    (item, i) => ({
                                                        value: item.value,
                                                        // Ưu tiên màu config; fallback nằm trong selector.
                                                        itemStyle: {
                                                            color: chartDeviceColors[
                                                                i
                                                            ],
                                                        },
                                                    })
                                                ),
                                                type: 'bar',
                                                colorBy: 'data',
                                                itemStyle: {
                                                    borderRadius: [
                                                        50, 50, 0, 0,
                                                    ],
                                                },
                                                barWidth: 36,
                                            },
                                        ],
                                    }}
                                />
                            </Box>
                        </Grid>
                        <Grid item md={userRole !== 'client' ? 4 : 6}>
                            <Box
                                sx={{
                                    minHeight: '414px',
                                    backgroundColor: common.white,
                                    padding: 4,
                                    borderRadius: '30px',
                                }}
                            >
                                <Typography variant="h6" sx={styles.boxTitle}>
                                    Khu vực
                                </Typography>
                                <PieDoughnutChart
                                    option={{
                                        color: chartRegionColors,
                                        title: '',
                                        tooltip: {
                                            trigger: 'item',
                                        },
                                        legend: {
                                            orient: 'vertical',
                                            top: 'center',
                                            left: '55%',
                                            icon: 'circle',
                                        },
                                        series: [
                                            {
                                                name: 'Khu vực',
                                                type: 'pie',
                                                radius: ['45%', '65%'],
                                                center: ['25%', '50%'],
                                                avoidLabelOverlap: false,
                                                label: {
                                                    color: '#000',
                                                    fontSize: '24',
                                                    position: 'center',
                                                    fontWeight: 'bold',
                                                    formatter: () => {
                                                        return numeral(
                                                            chartAreaData.data.reduce(
                                                                (acc, cur) =>
                                                                    acc +
                                                                    cur.value,
                                                                0
                                                            )
                                                        ).format('0a');
                                                    },
                                                },
                                                labelLine: {
                                                    show: false,
                                                },

                                                data: chartAreaData.data,
                                            },
                                        ],
                                    }}
                                />
                            </Box>
                        </Grid>
                        {userRole !== 'client' && (
                            <Grid item md={4}>
                                <Box
                                    sx={{
                                        minHeight: '414px',
                                        backgroundColor: common.white,
                                        padding: 4,
                                        borderRadius: '30px',
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={styles.boxTitle}
                                    >
                                        Tài khoản
                                    </Typography>
                                    <LineChart
                                        style={{ width: '100%' }}
                                        option={{
                                            color: chartAccountColors,
                                            title: {
                                                text: '',
                                            },
                                            tooltip: {
                                                trigger: 'axis',
                                                axisPointer: {
                                                    type: 'cross',
                                                },
                                            },

                                            toolbox: {
                                                show: false,
                                            },
                                            grid: {
                                                left: '0%',
                                                top: '5%',
                                                right: '5%',
                                                bottom: '15%',
                                                containLabel: true,
                                            },
                                            legend: {
                                                itemGap: 50,
                                                icon: 'square',
                                                top: 'bottom',
                                                data: [
                                                    'Tài khoản hệ thống',
                                                    'Khách hàng',
                                                ],
                                                itemWidth: 22,
                                                itemHeight: 22,
                                            },
                                            xAxis: {
                                                type: 'category',
                                                boundaryGap: false,
                                                data: chartAccountData.xAxis,
                                            },
                                            yAxis: {
                                                type: 'value',
                                            },
                                            series: [
                                                {
                                                    name: 'Tài khoản hệ thống',
                                                    type: 'line',
                                                    stack: 'Total',
                                                    smooth: true,
                                                    data: chartAccountData.data
                                                        .system,
                                                },
                                                {
                                                    name: 'Khách hàng',
                                                    type: 'line',
                                                    stack: 'Total',
                                                    smooth: true,
                                                    data: chartAccountData.data
                                                        .client,
                                                },
                                            ],
                                        }}
                                    />
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                    <Grid container mt={1} spacing={4}>
                        <Grid item md={6}>
                            <Box
                                sx={{
                                    minHeight: '500px',
                                    backgroundColor: common.white,
                                    padding: 3,
                                    borderRadius: '30px',
                                }}
                            >
                                <Typography variant="h6" sx={styles.boxTitle}>
                                    Chiến dịch
                                </Typography>
                                <TableContainer
                                    component={Paper}
                                    sx={{
                                        boxShadow: 'none',
                                        border: 'none',
                                    }}
                                >
                                    <Table sx={{ borderCollapse: 'collapse' }}>
                                        <TableHead>
                                            <TableRow>
                                                {[
                                                    'ID',
                                                    'Chiến dịch',
                                                    'Trạng thái',
                                                    'Tiến độ thu thập dữ liệu',
                                                    'Dữ liệu thu thập',
                                                ].map((cell, index) => (
                                                    <TableCell
                                                        key={index}
                                                        align={
                                                            index === 4
                                                                ? 'center'
                                                                : 'left'
                                                        }
                                                        sx={{
                                                            border: 'none',
                                                            fontWeight: 600,
                                                            fontSize: '18px',
                                                            lineHeight:
                                                                '21.78px',
                                                        }}
                                                    >
                                                        {cell}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {listCampaignData.map(
                                                (row, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell
                                                            align="left"
                                                            sx={{
                                                                border: 'none',
                                                            }}
                                                        >
                                                            {row.id}
                                                        </TableCell>
                                                        <TableCell
                                                            align="left"
                                                            sx={{
                                                                border: 'none',
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontSize:
                                                                        '18px',
                                                                    fontWeight: 500,
                                                                    lineHeight:
                                                                        '21.78px',
                                                                }}
                                                            >
                                                                {row.name}
                                                            </Typography>{' '}
                                                            {row.campaignTime}
                                                        </TableCell>
                                                        <TableCell
                                                            align="left"
                                                            sx={{
                                                                border: 'none',
                                                            }}
                                                        >
                                                            <CampaignStatus
                                                                status={
                                                                    row.status
                                                                }
                                                            />
                                                        </TableCell>
                                                        <TableCell
                                                            align="left"
                                                            sx={{
                                                                border: 'none',
                                                            }}
                                                        >
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={
                                                                    row.progress
                                                                }
                                                                sx={{
                                                                    height: 6,
                                                                    borderRadius: 10,
                                                                    backgroundColor:
                                                                        '#0000001A',
                                                                    '& .MuiLinearProgress-bar':
                                                                        {
                                                                            backgroundColor:
                                                                                'primary.main',
                                                                        },
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell
                                                            align="center"
                                                            sx={{
                                                                border: 'none',
                                                            }}
                                                        >
                                                            <Chip
                                                                label={
                                                                    row.totalData
                                                                }
                                                                sx={{
                                                                    backgroundColor:
                                                                        'var(--color-primary-light-50)',
                                                                    borderRadius:
                                                                        '5px',
                                                                    minWidth:
                                                                        '76px',
                                                                    minHeight:
                                                                        '42px',
                                                                    fontSize:
                                                                        '14px',
                                                                    fontWeight: 600,
                                                                    lineHeight:
                                                                        '16.94px',
                                                                    color: 'primary.main',
                                                                }}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                )
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                    }}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        setListCampaignPage((prev) => prev + 1);
                                    }}
                                >
                                    <Typography sx={styles.expandMore}>
                                        Xem thêm <ExpandMoreIcon />
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item md={6}>
                            <Box
                                sx={{
                                    minHeight: '500px',
                                    backgroundColor: common.white,
                                    padding: 3,
                                    borderRadius: '30px',
                                }}
                            >
                                <Typography variant="h6" sx={styles.boxTitle}>
                                    Thanh toán
                                </Typography>
                                <TableContainer
                                    component={Paper}
                                    sx={{
                                        boxShadow: 'none',
                                        border: 'none',
                                    }}
                                >
                                    <Table sx={{ borderCollapse: 'collapse' }}>
                                        <TableHead>
                                            <TableRow>
                                                {[
                                                    'ID',
                                                    'Thanh toán',
                                                    'Nội dung',
                                                    'Trạng thái',
                                                ].map((cell, index) => (
                                                    <TableCell
                                                        key={index}
                                                        align={
                                                            index === 4
                                                                ? 'center'
                                                                : 'left'
                                                        }
                                                        sx={{
                                                            border: 'none',
                                                            fontWeight: 600,
                                                            fontSize: '18px',
                                                            lineHeight:
                                                                '21.78px',
                                                        }}
                                                    >
                                                        {cell}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {listPaymentData.map((row) => (
                                                <TableRow key={row.id}>
                                                    <TableCell
                                                        align="left"
                                                        sx={{
                                                            border: 'none',
                                                        }}
                                                    >
                                                        {row.id}
                                                    </TableCell>
                                                    <TableCell
                                                        align="left"
                                                        sx={{
                                                            border: 'none',
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize:
                                                                    '18px',
                                                                fontWeight: 500,
                                                                lineHeight:
                                                                    '21.78px',
                                                            }}
                                                        >
                                                            {convertValuePaymentType(
                                                                row.paymentType
                                                            )}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell
                                                        align="left"
                                                        sx={{
                                                            border: 'none',
                                                        }}
                                                    >
                                                        <Typography
                                                            component="strong"
                                                            fontWeight="600"
                                                            fontSize={'14px'}
                                                            lineHeight={'17px'}
                                                        >
                                                            {row.productName}
                                                        </Typography>
                                                        <br />
                                                        <Typography
                                                            component="span"
                                                            fontSize={'14px'}
                                                            lineHeight={'17px'}
                                                        >
                                                            {row.productNote}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell
                                                        align="left"
                                                        sx={{
                                                            border: 'none',
                                                        }}
                                                    >
                                                        <PaymentStatus
                                                            status={row.status}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                    }}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        setListPaymentPage((prev) => prev + 1);
                                    }}
                                >
                                    <Typography sx={styles.expandMore}>
                                        Xem thêm <ExpandMoreIcon />
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </Box>
    );
};
const styles = createStyleSheet({
    boxTitle: {
        fontWeight: 'bold',
        fontSize: '22px',
    },
    expandMore: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '20px',
        fontWeight: 500,
        lineHeight: '24.2px',
        color: 'primary.main',
        cursor: 'pointer',
    },
});

export default Overview;
